import type { FormFieldOption } from '@/components/Form/types';
import type {
  FormFieldsetStoryblok,
  FormInputStoryblok,
  FormPageStoryblok,
  FormPostcodeStoryblok,
  FormRadioGroupStoryblok,
  FormSectionStoryblok,
  FormSelectStoryblok,
  FormStoryblok,
  FormStreetStoryblok,
} from '@/types/component-types-sb';

// Union types of items that can appear in a page's content or a section's fields
// These use the strict Storyblok types that are generated.
export type PageContentItem = NonNullable<FormPageStoryblok['content']>[number];
export type SectionFieldItem = NonNullable<FormSectionStoryblok['fields']>[number];
export type AnyBlock = PageContentItem | SectionFieldItem;

// Components that support cdp_field according to the generated types
export type CdpCapable =
  FormInputStoryblok | FormPostcodeStoryblok | FormRadioGroupStoryblok | FormSelectStoryblok | FormStreetStoryblok;

function isSectionType(b: AnyBlock): b is FormSectionStoryblok | FormFieldsetStoryblok {
  return !!b && (b.component === 'FormSection' || b.component === 'FormFieldset');
}

function isCdpCapable(b: AnyBlock): b is CdpCapable {
  return (
    !!b &&
    (b.component === 'FormInput' ||
      b.component === 'FormPostcode' ||
      b.component === 'FormRadioGroup' ||
      b.component === 'FormSelect' ||
      b.component === 'FormStreet')
  );
}

// Helperfunction, for extracting the options list from a storyblok field
export function getOptionsForField(blok: FormStoryblok, fieldId: string): FormFieldOption[] {
  const options: FormFieldOption[] = [];

  const collectOptionsForField = (arr?: AnyBlock[]) => {
    if (!Array.isArray(arr)) return [];
    arr.forEach((b) => {
      if (!b) return [];

      if (isSectionType(b)) {
        // Dive into nested section fields
        collectOptionsForField(b.fields as AnyBlock[] | undefined);
      }

      if ((b.component === 'FormRadioGroup' || b.component === 'FormSelect') && b.id === fieldId) {
        b.options.forEach((option) => {
          options.push({ value: String(option.value), label: String(b.label) });
        });
      }
    });
  };

  // Walk all pages and their content arrays
  const pages = blok.content as FormPageStoryblok[] | undefined;
  pages?.forEach((page) => {
    collectOptionsForField(page?.content as AnyBlock[] | undefined);
  });
  return options;
}

export function normalizeString(input: unknown): string {
  return String(input ?? '')
    .normalize('NFKD') // separte diakritica
    .replace(/[\u0300-\u036f]/g, '') // remove diakritica
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

export function resolveToAllowedOption(cdpValue: unknown, options: FormFieldOption[]): string | undefined {
  const n = normalizeString(cdpValue);
  if (!n) return undefined;

  const normalizedOptions = options.map((o) => ({
    raw: o.value,
    nValue: normalizeString(o.value),
    nLabel: normalizeString(o.label ?? ''),
  }));

  // 1) Exact (normalized) against value
  const exactByValue = normalizedOptions.find((o) => o.nValue === n);
  if (exactByValue) return exactByValue.raw;

  // 2) Exact (normalized) against label
  const exactByLabel = normalizedOptions.find((o) => o.nLabel === n);
  if (exactByLabel) return exactByLabel.raw;

  // 3) Soft-Match (z. B. startsWith), but only if distinct
  const softMatches = normalizedOptions.filter((o) => o.nValue.startsWith(n) || o.nLabel.startsWith(n));
  if (softMatches.length === 1) return softMatches[0]!.raw;

  return undefined; // no safe match
}

/**
 * Traverses a FormStoryblok structure and collects fields that declare `cdp_field`.
 * Returns a flat list of objects with the field id and its cdpField key.
 */
export function collectCdpFields(blok: FormStoryblok): { id: string; cdpField: string; type: string }[] {
  const collected: { id: string; cdpField: string; type: string }[] = [];

  const collectFieldsInArray = (arr?: AnyBlock[]) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((b) => {
      if (!b) return;

      if (isSectionType(b)) {
        // Dive into nested section fields
        collectFieldsInArray(b.fields as AnyBlock[] | undefined);
      }

      if (isCdpCapable(b) && b.id && b.cdp_field != null) {
        collected.push({ id: b.id, cdpField: String(b.cdp_field), type: b.component });
      }
    });
  };

  // Walk all pages and their content arrays
  const pages = blok.content as FormPageStoryblok[] | undefined;
  pages?.forEach((page) => {
    collectFieldsInArray(page?.content as AnyBlock[] | undefined);
  });

  return collected;
}
